<template>
  <section class="h-full mx-4" id="representativesManagement">
    <BaseTable
      v-if="representatives"
      :headers="representativeHeaders"
      :list="representatives"
      :total-records="representativesTotalCount"
      table-id="representatives-management"
      :items-per-page="representativesItemsPerPage"
      :selected-page="representativesPageNumber"
      @refresh-data="onRefreshData"
      @deleteMultiselect="onDeleteMultiselect"
      @add-new="onAddNewRepresentative"
      clickable
      @table-item-clicked="onItemClick"
      @apply-filters="onApplyFilters"
      @reset-filters="onResetFilters">
      <template #actions="{ item }">
        <div class="flex items-center justify-center gap-1">
          <ActionButton
            :isEnabled="item.isFromManager && !item.deleted"
            color="red"
            :icon="IconTrashOutline"
            @action="deleteSingleConfirm(item)" />
        </div>
      </template>
      <template #filters>
        <section class="flex flex-col items-start justify-start w-full max-w-xl py-4 mx-auto">
          <div>
            <label class="space-y-2 text-sm tracking-wide text-gray-400 uppercase">{{
              $t('baseObject.isActive')
            }}</label>
            <BaseTriStateToggle
              :label="$t('baseObject.isActive')"
              name="isActive"
              v-model="filterIsActive" />
          </div>
        </section>
      </template>
    </BaseTable>
    <BaseModalConfirm
      v-if="confirmDeleteSingleModalActive"
      @cancel="confirmDeleteSingleModalActive = false"
      @confirm="onDeleteSingle()">
      <template #title>{{ $t('common.actions.delete') }}</template>
      <template #text>
        {{
          $t('messages.info.deleteSingle', { item: representativeToDeleteSingle?.code })
        }}</template
      >
      <template #buttonText>{{ $t('common.actions.delete') }}</template>
    </BaseModalConfirm>
  </section>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue';
import {
  getRepresentativesWithConditions,
  removeRepresentativesByIds,
} from '@/services/api/traders';
import { useToastStore } from '@/stores/toast';
import BaseTable from '@/components/common/BaseTable.vue';
import BaseModalConfirm from '@/components/common/BaseModalConfirm.vue';
import type { Representative } from '@/types/Traders';
import type { QueryParams, TableFilterData } from '@/types/Base';
import router from '@/router';
import { v4 as uuidv4 } from 'uuid';
import ActionButton from '@/components/common/ActionButton.vue';
import IconTrashOutline from '@/assets/icons/IconTrashOutline.vue';
import { useI18n } from 'vue-i18n';
import { useStateStore } from '@/stores/state';
import BaseTriStateToggle from '@/components/common/BaseTriStateToggle.vue';

const { t: $t } = useI18n({ useScope: 'global' });
const toastStore = useToastStore();
const representatives = ref<Representative[]>();
const representativesTotalCount = ref<number>();
const representativesItemsPerPage = ref(50);
const representativesPageNumber = ref(1);
const confirmDeleteSingleModalActive = ref(false);
const representativeToDeleteSingle = ref<Representative>();
const filterIsActive = ref<boolean | undefined>();

const representativeHeaders = [
  { value: 'code', text: `${$t('baseObject.code')}`, type: 'text', active: true },
  { value: 'name', text: `${$t('baseObject.name')}`, type: 'text', active: true },
  { value: 'trader.name', text: `${$t('customer.traderName')}`, type: 'text', active: true },
  {
    value: 'trader.taxNumber',
    text: `${$t('customer.traderTaxNumber')}`,
    type: 'text',
    active: true,
  },
  { value: 'trader.address', text: $t('customer.traderAddress'), type: 'text', active: true },
  { value: 'trader.job', text: $t('customer.traderJob'), type: 'text', active: true },
  { value: 'trader.code', text: $t('customer.traderCode'), type: 'text', active: true },
  { value: 'trader.zipCode', text: $t('customer.traderZipCode'), type: 'text', active: true },
  { value: 'company.name', text: $t('baseObject.name'), type: 'text', active: true },
  { value: 'company.code', text: $t('baseObject.code'), type: 'text', active: true },
  { value: 'actions', text: $t('baseObject.actions'), active: true },
];

const { getTableSelectedPage } = useStateStore();

onMounted(async () => {
  const storedTableData = getTableSelectedPage('representatives-management');
  if (storedTableData) {
    representativesItemsPerPage.value = storedTableData.itemsPerPage;
    representativesPageNumber.value = storedTableData.page;
  }
  await loadData({
    itemsPerPage: representativesItemsPerPage.value,
    sortColumn: 'code',
    sortAscending: true,
    page: representativesPageNumber.value,
  });
});

async function onRefreshData(data: TableFilterData) {
  const sortColumn = data.column !== undefined ? representativeHeaders[data.column]?.value : 'code';
  const sortAscending = data.order === 'asc' || data.order === undefined;
  const params: QueryParams = {
    page: data.page || 1,
    itemsPerPage: data.itemsPerPage || 50,
    query: data.query || '',
    sortColumn: sortColumn,
    sortAscending: sortAscending,
    isActive: data.isActive,
  };

  await loadData(params);
}

async function loadData(params: QueryParams) {
  const res = await getRepresentativesWithConditions(params);
  if (res) {
    representatives.value = res.data;
    representativesTotalCount.value = res.count;
  }
}

async function onDeleteSingle() {
  if (!representativeToDeleteSingle.value) return;
  confirmDeleteSingleModalActive.value = false;
  await deleteRepresentatives([representativeToDeleteSingle.value.id]);
}

async function onDeleteMultiselect(items: Representative[]) {
  const idsToDelete = items.filter((x) => x.isFromManager == true).map((item) => item.id);
  if (idsToDelete.length) await deleteRepresentatives(idsToDelete);
}

async function deleteRepresentatives(idsToDelete: string[]) {
  const res = await removeRepresentativesByIds(idsToDelete);
  if (!res && representatives.value)
    representatives.value = representatives.value.filter(
      (type: Representative) => !idsToDelete.includes(type.id)
    );
  res
    ? toastStore.error({ text: res })
    : toastStore.success({ text: $t('messages.success.successfullyDeleted') });
}

function deleteSingleConfirm(item: Representative) {
  representativeToDeleteSingle.value = item;
  confirmDeleteSingleModalActive.value = true;
}

function onAddNewRepresentative() {
  router.push({ name: 'representatives-details', params: { id: uuidv4() } });
}

function onItemClick(item: Representative) {
  router.push({ name: 'representatives-details', params: { id: item.id } });
}

function onApplyFilters(data: TableFilterData) {
  data.isActive = filterIsActive.value ?? undefined;
  onRefreshData(data);
}

function onResetFilters() {
  filterIsActive.value = undefined;
  onRefreshData({});
}
</script>
